import { CircularProgress } from '@mui/material'
import { GET_MY_FEEDS_TABLE, getMyFeeds } from 'api'
import { BoxPartners, Button, Input } from 'components'
import { useSearchDebounce } from 'hooks'
import {
	AddFeedInputs,
	AddFeedProps,
	Icons,
	NetworkMyFeedsTableItem
} from 'interfaces'
import React, { useEffect, useMemo, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useInfiniteQuery } from 'react-query'
import { useInView } from 'react-intersection-observer'
import Cookies from 'universal-cookie'

export const AddFeed: React.FC<AddFeedProps> = ({
	totalData,
	setAddFeed,
	onClickClose,
	setTotalData,
	setSlideAudience
}): JSX.Element => {
	const cookies = new Cookies()
	const userId = cookies.get('userId')
	const organizationId = cookies.get('organization_id')
	const { ref, inView } = useInView()

	const [feedsSelected, setFeedsSelected] = useState<number[]>(() => [
		...totalData.delivery_enhancements.audience.feeds
	])

	const { watch, register, handleSubmit } = useForm<AddFeedInputs>({
		mode: 'all'
	})

	const search = watch('search')
	const { inputSearch, handleChangeDebounce } = useSearchDebounce(search)

	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
		useInfiniteQuery(
			[GET_MY_FEEDS_TABLE, { userId, organizationId }, inputSearch],
			({ pageParam = 1 }) =>
				getMyFeeds(organizationId, {
					page: pageParam,
					search: inputSearch.trim()
				}),
			{
				getPreviousPageParam: firstPage =>
					firstPage?.page ? firstPage.page : undefined,
				getNextPageParam: lastPage =>
					lastPage && lastPage.page < lastPage.pages
						? lastPage.page + 1
						: undefined
			}
		)

	useEffect(() => {
		if (inView && hasNextPage) fetchNextPage()
	}, [inView])

	const listFeeds = useMemo(
		() => data?.pages.flatMap(page => page?.items ?? []) ?? [],
		[data]
	)

	const handleClickFeed = (feed: NetworkMyFeedsTableItem): void => {
		if (feedsSelected.includes(feed.id)) {
			setFeedsSelected(feedsSelected.filter(item => item !== feed.id))
		} else {
			setFeedsSelected([...feedsSelected, feed.id])
		}
	}

	const onSubmit: SubmitHandler<AddFeedInputs> = () => {
		setTotalData({
			...totalData,
			delivery_enhancements: {
				...totalData.delivery_enhancements,
				audience: {
					...totalData.delivery_enhancements.audience,
					feeds: feedsSelected
				}
			}
		})
		setAddFeed(false)
		setSlideAudience(true)
	}

	return (
		<form
			onSubmit={handleSubmit(onSubmit)}
			className="mt-5 flex flex-col justify-between h-full"
		>
			<div className="h-[93%]">
				<Input
					name="search"
					className="mb-4"
					borderFull={false}
					register={register}
					leftIcon={Icons.search}
					inputClassName="rounded-[10px]"
					placeholder="Find Your Feeds"
					onChangeCustom={handleChangeDebounce}
				/>
				<div className="overflow-y-scroll hide-scroll-bar max-h-[90%] pt-3">
					{isLoading ? (
						<div className="flex items-center justify-center w-full h-[400px] py-2 px-1">
							<CircularProgress disableShrink />
						</div>
					) : (
						listFeeds.map(item => (
							<BoxPartners
								key={item.id}
								id={`${item.id}`}
								title={item.name}
								itemsSeleted={feedsSelected}
								onClick={() => handleClickFeed(item)}
							/>
						))
					)}
					<div ref={ref}>
						{isFetchingNextPage && <CircularProgress disableShrink />}
					</div>
				</div>
			</div>

			<div className="flex items-center justify-between">
				<Button
					type="button"
					label="Cancel"
					color="Transparent"
					className="!min-w-[130px]"
					onClick={onClickClose}
				/>
				<Button
					label="Save"
					type="submit"
					color="Primary"
					className="!min-w-[130px]"
				/>
			</div>
		</form>
	)
}
